import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { sendToolEvent } from '../lib/analytics'

type AppSection = {
  heading: string
  body: string
  headingEn: string
  bodyEn: string
}

type ToolMatePageContent = {
  title: string
  titleEn: string
  intro: string
  introEn: string
  sections: AppSection[]
}

const supportEmail = 'contact@parentool.cn'

const toolMatePages: Record<'privacy' | 'terms' | 'support', ToolMatePageContent> = {
  privacy: {
    title: '隐私政策',
    titleEn: 'Privacy Policy',
    intro: '简单工具箱是一款面向日常生活场景的小工具合集应用，当前主要提供礼金记录、压岁钱记录、倒数日、家庭待办和亲戚计算器等功能。我们尽量减少数据收集，并以本地存储为主。',
    introEn: 'ToolMate is a lightweight collection of everyday utilities, currently including gift record tracking, lucky money tracking, countdowns, family to-dos, and a relative calculator. We minimize data collection and primarily rely on on-device storage.',
    sections: [
      {
        heading: '数据存储方式',
        body: '当前版本的简单工具箱主要将你的记录和设置保存在设备本地，不要求注册账号，也不提供云端同步。',
        headingEn: 'How Data Is Stored',
        bodyEn: 'The current version of ToolMate stores your records and settings primarily on your device. It does not require account registration and does not provide cloud sync at this stage.'
      },
      {
        heading: '通知权限用途',
        body: '如果你启用了本地通知权限，简单工具箱仅会将其用于你主动设置的提醒事项，例如倒数日或待办提醒。',
        headingEn: 'Notification Permission',
        bodyEn: 'If you enable local notifications, ToolMate uses them only for reminders you create, such as countdown reminders or family task reminders.'
      },
      {
        heading: '我们不主动收集的信息',
        body: '当前版本不要求你提交姓名、手机号、邮箱、身份证号等账号资料，也不会因为使用礼金记录、压岁钱记录等功能而主动上传你的内容到服务器。',
        headingEn: 'Information We Do Not Intentionally Collect',
        bodyEn: 'The current version does not require your name, phone number, email address, ID number, or similar account information, and it does not intentionally upload your gift or lucky money records to a server.'
      },
      {
        heading: '第三方服务',
        body: '当前版本不包含广告、支付或云同步服务。如果后续版本引入新的第三方能力，我们会同步更新本政策。',
        headingEn: 'Third-Party Services',
        bodyEn: 'The current version does not include advertising, payments, or cloud synchronization services. If future versions introduce new third-party capabilities, this policy will be updated accordingly.'
      },
      {
        heading: '联系我们',
        body: `如果你对隐私政策有疑问，可以通过 ${supportEmail} 联系我们。`,
        headingEn: 'Contact Us',
        bodyEn: `If you have any questions about this privacy policy, contact us at ${supportEmail}.`
      }
    ]
  },
  terms: {
    title: '用户协议',
    titleEn: 'Terms of Use',
    intro: '简单工具箱为个人和家庭场景提供日常记录与小工具能力。使用本应用即表示你同意按本协议使用相关功能。',
    introEn: 'ToolMate provides everyday recording and utility features for personal and family use. By using the app, you agree to these Terms of Use.',
    sections: [
      {
        heading: '使用范围',
        body: '你可以将简单工具箱用于日常礼金记录、压岁钱记录、倒数日、家庭待办和亲戚称谓辅助等非商业、个人或家庭场景。',
        headingEn: 'Permitted Use',
        bodyEn: 'You may use ToolMate for non-commercial personal or family scenarios such as gift record tracking, lucky money records, countdowns, family to-dos, and relative relationship assistance.'
      },
      {
        heading: '记录内容责任',
        body: '你需要自行确认录入内容的准确性，并自行保管重要记录。对于礼金、压岁钱、家庭安排等信息，建议你定期自行备份或核对。',
        headingEn: 'Responsibility for Your Records',
        bodyEn: 'You are responsible for the accuracy of the information you enter and for keeping important records safe. For gift records, lucky money records, and family arrangements, periodic review or backup is recommended.'
      },
      {
        heading: '功能更新与变更',
        body: '我们可能根据产品迭代增加、调整或下线部分功能，未来也可能增加新的日常小工具。',
        headingEn: 'Feature Changes',
        bodyEn: 'We may add, modify, or remove certain features as the product evolves, and more daily utility tools may be introduced in future releases.'
      },
      {
        heading: '责任限制',
        body: '简单工具箱提供的是一般性的记录和辅助工具，不对因误删、误记、设备故障或你未核对结果导致的损失承担责任。',
        headingEn: 'Limitation of Liability',
        bodyEn: 'ToolMate provides general-purpose recording and utility assistance. We are not liable for losses caused by mistaken entries, accidental deletion, device issues, or failure to verify results.'
      },
      {
        heading: '联系我们',
        body: `如对本协议有疑问或需要反馈，请发送邮件至 ${supportEmail}。`,
        headingEn: 'Contact',
        bodyEn: `If you have questions about these terms or need support, contact ${supportEmail}.`
      }
    ]
  },
  support: {
    title: '技术支持',
    titleEn: 'Technical Support',
    intro: '简单工具箱（ToolMate）是一款面向日常生活的小工具合集 App，当前主要覆盖礼金记录、压岁钱记录、倒数日、家庭待办和亲戚计算器等场景，后续还会增加更多工具。',
    introEn: 'ToolMate is a daily-life utility app currently focused on gift records, lucky money records, countdowns, family to-dos, and a relative calculator, with more tools planned in future updates.',
    sections: [
      {
        heading: '支持方式',
        body: `如遇到问题、建议新功能或反馈异常，请发邮件至 ${supportEmail}。`,
        headingEn: 'How to Reach Support',
        bodyEn: `For bugs, feature requests, or general support, email us at ${supportEmail}.`
      },
      {
        heading: '反馈时建议提供的信息',
        body: '建议附上设备型号、系统版本、应用版本、问题发生步骤，以及必要时的截图，这样更容易定位问题。',
        headingEn: 'What to Include in a Support Request',
        bodyEn: 'Please include your device model, system version, app version, reproduction steps, and screenshots when relevant. This makes it much easier to diagnose the issue.'
      },
      {
        heading: '当前工具范围',
        body: '当前主要包括礼金记录、压岁钱记录、倒数日、家庭待办、亲戚计算器等功能。',
        headingEn: 'Current Tool Categories',
        bodyEn: 'Current tools include gift record tracking, lucky money records, countdowns, family to-dos, and a relative calculator.'
      },
      {
        heading: '常见说明',
        body: '当前版本以本地数据存储为主。如果你更换设备或删除应用，本地未备份的数据可能无法恢复。',
        headingEn: 'Common Notes',
        bodyEn: 'The current version primarily stores data locally. If you change devices or delete the app, unbacked local data may not be recoverable.'
      }
    ]
  }
}

const pageLinks = [
  { id: 'privacy', href: '/app/toolmate/privacy', label: '隐私政策' },
  { id: 'terms', href: '/app/toolmate/terms', label: '用户协议' },
  { id: 'support', href: '/app/toolmate/support', label: '技术支持' }
] as const

type ToolMateAppPageProps = {
  pageId: keyof typeof toolMatePages
}

const ToolMateAppPage = ({ pageId }: ToolMateAppPageProps) => {
  const page = toolMatePages[pageId]

  useEffect(() => {
    sendToolEvent('tool_view', {
      tool_id: `toolmate-${pageId}`,
      tool_name: `ToolMate ${page.title}`,
      tool_category: 'app-support',
      action_type: 'view'
    })
  }, [page.title, pageId])

  return (
    <div className="bg-stone-100 px-4 py-8 md:px-6 md:py-10">
      <article className="mx-auto max-w-4xl overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm">
        <header className="border-b border-stone-200 bg-stone-50 px-6 py-6 md:px-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-stone-500">Simple Toolbox App</p>
          <div className="mt-3">
            <h1 className="text-3xl font-bold text-stone-950">简单工具箱</h1>
            <p className="mt-1 text-base font-medium text-stone-600">ToolMate</p>
          </div>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-stone-600">{page.intro}</p>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-stone-500">{page.introEn}</p>
        </header>

        <nav className="border-b border-stone-200 px-6 py-4 md:px-8">
          <div className="flex flex-wrap gap-3">
            {pageLinks.map(link => {
              const isActive = link.id === pageId
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-stone-900 bg-stone-900 text-white'
                      : 'border-stone-300 text-stone-700 hover:border-stone-500 hover:text-stone-950'
                  }`}
                >
                  {link.label}
                </Link>
              )
            })}
          </div>
        </nav>

        <div className="px-6 py-8 md:px-8">
          <section>
            <h2 className="text-2xl font-semibold text-stone-950">{page.title}</h2>
            <p className="mt-2 text-sm font-medium text-stone-500">{page.titleEn}</p>
          </section>

          <div className="mt-8 space-y-8">
            {page.sections.map(section => (
              <section key={section.heading}>
                <h3 className="text-lg font-semibold text-stone-950">{section.heading}</h3>
                <p className="mt-2 text-sm leading-7 text-stone-700">{section.body}</p>
                <h4 className="mt-4 text-sm font-semibold uppercase tracking-wide text-stone-500">{section.headingEn}</h4>
                <p className="mt-2 text-sm leading-7 text-stone-600">{section.bodyEn}</p>
              </section>
            ))}
          </div>
        </div>

        <footer className="border-t border-stone-200 bg-stone-50 px-6 py-5 text-sm text-stone-600 md:px-8">
          <p>Support: {supportEmail}</p>
          <p className="mt-1">© 2026 ToolMate / 简单工具箱</p>
        </footer>
      </article>
    </div>
  )
}

export default ToolMateAppPage
